import z from 'zod';
import * as R from 'remeda';
import { LlmParams } from '../lib/openAi.js';
import { DateTimeSchema, ObjectIdSchema, ObjectIdString } from '../common.js';
import {
  LlmResponseSchemaConfig,
  LLmResponseSchemaNameOption as SchemaName,
} from '../llmResponseSchemas.js';

export { LlmResponseSchemaConfig };

export const PromptCategory = z.enum([
  'candidate',
  'contact',
  'company',
  'opportunity',
  'analytics',
  'other',
]);
export type PromptCategory = z.infer<typeof PromptCategory>;

export const PromptStatus = z.enum(['enabled', 'draft', 'disabled']);
export type PromptStatus = z.infer<typeof PromptStatus>;

export const ILlmPrompt = z.object({
  _id: ObjectIdSchema,
  provider: z.string(),
  model: z.string(),
  name: z.string().optional(),
  category: PromptCategory,
  promptType: z.string(),
  previousVersion: ObjectIdSchema.optional(),
  status: PromptStatus,
  params: LlmParams,
  responseSchema: LlmResponseSchemaConfig.nullish(),
  templateInput: z.record(z.string(), z.unknown()).optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type ILlmPrompt<T extends SchemaName | null = SchemaName | null> = z.infer<
  typeof ILlmPrompt
> & { responseSchema: { name: T } };

export const LlmPromptDTO = z.looseObject({
  ...ILlmPrompt.shape,
  _id: ObjectIdString,
  previousVersion: ObjectIdString.optional(),
  createdAt: DateTimeSchema.optional(),
  updatedAt: DateTimeSchema.optional(),

  // To differentiate from InvalidLlmPromptDTO
  _isInvalid: z.literal(false).nullish(),
});
export type LlmPromptDTO = z.infer<typeof LlmPromptDTO>;

export const InvalidLlmPromptDTO = z.looseObject({
  ...LlmPromptDTO.shape,
  category: z.string().default('unknown'),
  status: z.string().default('disabled'),
  params: z.unknown(),
  responseSchema: z.unknown().nullish(),

  _isInvalid: z.literal(true),
  _errors: z.unknown(),
});
export type InvalidLlmPromptDTO = z.infer<typeof InvalidLlmPromptDTO>;

export const LlmPromptDTOMaybeInvalid = z.preprocess(
  (value) => {
    try {
      const { error, data } = LlmPromptDTO.safeParse(value);
      if (data) return data;
      if (!R.isObjectType(value)) return value;
      return InvalidLlmPromptDTO.parse({
        ...value,
        _isInvalid: true,
        _errors: z.formatError(error),
      });
    } catch (err) {
      return value;
    }
  },
  z.discriminatedUnion('_isInvalid', [LlmPromptDTO, InvalidLlmPromptDTO])
);
export type LlmPromptDTOMaybeInvalid = z.infer<typeof LlmPromptDTOMaybeInvalid>;

export const LlmPromptJsonData = z.object({
  name: z.string().optional(),
  category: PromptCategory.optional(),
  promptType: z.string().optional(),
  previousVersion: ObjectIdString.optional(),
  status: PromptStatus.optional(),
  provider: z.string(),
  model: z.string(),
  params: z.looseObject({}).optional(),
  templateInput: z.looseObject({}).nullish(),
  responseSchema: LlmResponseSchemaConfig.extend({
    name: z.string().or(z.null()),
  })
    .partial()
    .nullish(),
});
export type LlmPromptJsonData = z.infer<typeof LlmPromptJsonData>;
